import { useState } from "react";
import { Dialog, TextField } from "@mui/material";
import { useAuth } from "../../contexts/AuthContext";
import { authService } from "../../services";

interface PhoneOtpModalProps {
  open: boolean;
  onClose: () => void;
  onVerified: () => void;
}

type Step = "phone" | "otp";

const PHONE_REGEX = /^\d{10}$/;
const RESEND_COOLDOWN_SECONDS = 30;

const PhoneOtpModal = ({ open, onClose, onVerified }: PhoneOtpModalProps) => {
  const { loginWithOtp } = useAuth();

  const [step, setStep] = useState<Step>("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const resetState = () => {
    setStep("phone");
    setPhone("");
    setOtp("");
    setEmail("");
    setError("");
    setLoading(false);
    setCooldown(0);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const startCooldown = () => {
    setCooldown(RESEND_COOLDOWN_SECONDS);
    const interval = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendOtp = async () => {
    setError("");
    if (!PHONE_REGEX.test(phone)) {
      setError("Enter a valid 10-digit phone number");
      return;
    }
    try {
      setLoading(true);
      await authService.sendOtp({ phone });
      setStep("otp");
      startCooldown();
    } catch (err: any) {
      setError(err.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError("");
    if (!otp) {
      setError("Enter the OTP sent to your phone");
      return;
    }
    try {
      setLoading(true);
      await loginWithOtp(phone, otp, email || undefined);
      resetState();
      onVerified();
    } catch (err: any) {
      setError(err.message || "Failed to verify OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleChangeNumber = () => {
    setStep("phone");
    setOtp("");
    setError("");
    setCooldown(0);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <div className="p-8">
        <h2 className="text-2xl font-mazzardbold mb-2 text-gray-800">
          {step === "phone" ? "Verify your number" : "Enter OTP"}
        </h2>
        <p className="text-gray-600 mb-6 text-sm">
          {step === "phone"
            ? "We need to verify your mobile number before searching."
            : `We sent a 6-digit code to ${phone}.`}
        </p>

        {step === "phone" ? (
          <>
            <TextField
              label="Mobile number"
              value={phone}
              onChange={(e) =>
                setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))
              }
              fullWidth
              variant="outlined"
              sx={{ mb: 2.5 }}
              inputProps={{ inputMode: "numeric" }}
              autoFocus
            />
            {error && (
              <p className="text-red-600 text-sm mb-4">{error}</p>
            )}
            <button
              onClick={handleSendOtp}
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-800 to-blue-900 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Sending..." : "Send OTP"}
            </button>
          </>
        ) : (
          <>
            <TextField
              label="OTP"
              value={otp}
              onChange={(e) =>
                setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              fullWidth
              variant="outlined"
              sx={{ mb: 2.5 }}
              inputProps={{ inputMode: "numeric" }}
              autoFocus
            />
            <TextField
              label="Email (optional)"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              variant="outlined"
              sx={{ mb: 2.5 }}
            />
            {error && (
              <p className="text-red-600 text-sm mb-4">{error}</p>
            )}
            <button
              onClick={handleVerifyOtp}
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-800 to-blue-900 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Verifying..." : "Verify & Continue"}
            </button>
            <div className="flex justify-between mt-4 text-sm">
              <button
                onClick={handleChangeNumber}
                className="text-blue-800 hover:text-blue-900 font-semibold"
              >
                Change number
              </button>
              <button
                onClick={handleSendOtp}
                disabled={cooldown > 0 || loading}
                className="text-blue-800 hover:text-blue-900 font-semibold disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                {cooldown > 0 ? `Resend OTP in ${cooldown}s` : "Resend OTP"}
              </button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
};

export default PhoneOtpModal;
